import AddFavorite from 'components/AddFavorite';
import { ResourceTile } from 'components/ResourceTile/ResourceTile';
import WebsiteSplitter from 'components/WebsiteSplitter';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import c from './_fastFacts.module.scss';

import { animated, useTrail } from '@react-spring/web';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import i18n from 'i18n-react';

interface FastFact {
  url: string;
  title: string;
  subtitle: string;
  image: string;
  type: string;
  itemID: string;
  graduated_unlock_only: boolean;
}

interface FastFactItemProps {
  fact: FastFact;
  appId: number | string;
  graduated: string | null;
}

function FastFactItem({ fact, appId, graduated }: FastFactItemProps) {
  const { type, title, subtitle, image, itemID } = fact;

  const url: string | string[] = typeof fact.url === 'string' && fact.url.includes(',') ? fact.url.split(',') : fact.url;
 
  const locked = fact.graduated_unlock_only && !graduated;
  const lockedText: React.ReactNode = locked ? 'Complete Foundation to unlock these Exclusive Discounts' : '';

  return (
    <div className={c.card}>
      {!locked && <AddFavorite appId={appId} viewType="t" upID={itemID} />}
      <ResourceTile
        type={type}
        url={url}
        title={title}
        subtitle={subtitle}
        image={image}
        appId={appId}
        uniqueId={itemID}
        locked={locked}
        lockedText={lockedText}
      />
      {type === 'multi' && <WebsiteSplitter itemID={itemID} appID={appId} type="fastfacts" />}
      {url === '#noContent' && (
        <div id="noContent" style={{ display: 'none' }}>
          <h1>{i18n.translate('errors.comingSoon')}</h1>
          <p>{i18n.translate('errors.missingContent')}</p>
        </div>
      )}
    </div>
  );
}

export function FastFacts() {
  const { user } = useUserInfo();
  const { salesCompanionAppId } = useSettings();

  const fetchFastFacts = APIHandler.getFastFacts as (appId: number | string) => Promise<FastFact[]>;

  const { data: fastFacts = [], isFetched } = useQuery<FastFact[]>({
    queryKey: ['fastFacts', salesCompanionAppId],
    queryFn: () => {
      if (salesCompanionAppId !== undefined) {
        return fetchFastFacts(salesCompanionAppId);
      }
      return Promise.resolve([]);
    },
    select: (res) => res.filter((fact) => !fact.title.includes('HIDE_')),
    enabled: salesCompanionAppId !== undefined,
  });

  const trail = useTrail(fastFacts.length, {
    from: { opacity: 0, transform: 'translateY(20%)' },
    to: { opacity: 1, transform: 'translateY(0%)' },
    immediate: import.meta.env.MODE === 'test',
  });

  return (
    <div
      className={c.grid}
      data-testid="fast-facts-grid"
      data-fetch-complete={isFetched ? 'true' : 'false'}
      data-card-count={fastFacts.length}
    >
      {trail.map((style, id) => {
        const fact = fastFacts[id];
        if (!fact?.type) return null;

        const isLast = id === trail.length - 1;
        const testId = isLast ? 'fast-fact-card-last' : `fast-fact-card-${id}`;

        return (
          <animated.div
            data-testid={testId}
            key={id}
            className={clsx(c.animatedCard, { [c.directory]: fact.type === 'directory' })}
            style={style}
          >
            {salesCompanionAppId !== undefined && (
              <FastFactItem
                fact={fact}
                appId={salesCompanionAppId}
                graduated={(user?.training_graduated as string | null) ?? null}
              />
            )}
          </animated.div>
        );
      })}
    </div>
  );
}
